/**
 * useTheme hook - Provides theme state and utilities
 */

import {useEffect, useMemo} from 'react';
import {useUIStore} from '@voxapilot/shared';

type Theme = 'light' | 'dark' | 'system';

/**
 * Get the effective theme (resolves 'system' to actual theme)
 * Safely handles cases where matchMedia might not be available
 */
function getEffectiveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    // Check if matchMedia is available (should be in all modern browsers)
    if (typeof window !== 'undefined' && window.matchMedia) {
      try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
      } catch (e) {
        // If matchMedia fails, default to light mode
        console.warn('Failed to detect system theme preference, defaulting to light mode', e);
        return 'light';
      }
    }
    // Fallback for very old browsers without matchMedia support
    return 'light';
  }
  return theme;
}

/**
 * Hook to access and manage theme
 */
export function useTheme() {
  const {theme, setTheme} = useUIStore();

  // Get the effective theme (light or dark)
  const effectiveTheme = useMemo(() => getEffectiveTheme(theme), [theme]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const effective = getEffectiveTheme(theme);

    if (effective === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Listen to system preference changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return;
    
    // Check if matchMedia is available
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        const root = document.documentElement;
        if (mediaQuery.matches) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
      // Fallback for older browsers
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    } catch (e) {
      // If matchMedia fails, silently fail (theme will still work, just won't auto-update)
      console.warn('Failed to listen to system theme changes', e);
    }
  }, [theme]);

  return {
    theme,
    effectiveTheme,
    setTheme,
    isDark: effectiveTheme === 'dark',
    isLight: effectiveTheme === 'light',
  };
}

